import { FC, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { getIdModalState } from "@/entities/idModal";
import styles from "./CollectionDetailViewApps.module.scss";
import { ObserverWrapper } from "@/widgets/observerWrapper/ui/ObserverWrapper/ObserverWrapper";
import { useCurrentOrganization } from "@/Hooks";
import { AppCollectionAppsResponse } from "@/features/auth/types/auth";
import { useInView } from "react-intersection-observer";
import React from "react";
import { VariableSizeList as List } from "react-window";
import InfiniteLoader from "react-window-infinite-loader";
import AutoSizer from "react-virtualized-auto-sizer";
import ExploreReplayRow from "@/Views/Virtual/ExploreReplayRow";
import SelectedScreensPopup from "@/Views/Virtual/SelectedScreensPopup/SelectedScreensPopup";
import { useSelectedScreens } from "@/entities/selectedScreens";
import { useRemoveAppsFromCollectionMutation } from "@/features/auth/api/authAPI";
import { toast } from "react-toastify";
import Loader from "@/shared/ui/Loader/Loader";

interface CollectionDetailViewAppsProps {
  collectionApps: AppCollectionAppsResponse | null;
  setCollectionApps: React.Dispatch<
    React.SetStateAction<AppCollectionAppsResponse | null>
  >;
  loadMoreApps: () => Promise<void>;
  isFetchingMore: boolean;
  handleRemoveFromCollection: (appId: number) => void;
}

interface RowProps {
  index: number;
  style: React.CSSProperties;
}

interface ListRef {
  resetAfterIndex: (index: number) => void;
}

const CollectionDetailViewApps: FC<CollectionDetailViewAppsProps> = ({
  collectionApps,
  setCollectionApps,
  loadMoreApps,
  isFetchingMore,
  handleRemoveFromCollection,
}) => {
  const { openModalId } = useSelector(getIdModalState);
  const currentOrganization = useCurrentOrganization();
  const modalRef = useRef<HTMLDivElement>(null);

  // Use ref to store current data for Column component access
  const collectionAppsRef = useRef(collectionApps);
  collectionAppsRef.current = collectionApps;

  // Function to update replay screens saved status in collection apps data
  const updateReplayScreensSavedStatus = useCallback(
    (
      screenIds: number[],
      isSaved: boolean,
      savedPointIds: Record<number, number> | null,
    ) => {
      setCollectionApps((prevCollectionApps) => {
        if (!prevCollectionApps) return prevCollectionApps;

        return {
          ...prevCollectionApps,
          results: prevCollectionApps.results.map((app) => {
            // Check if this app has any screens that need to be updated
            const updatedReplays = app.replays.map((screen) => {
              if (screenIds.includes(screen.id)) {
                return {
                  ...screen,
                  is_saved: isSaved,
                  is_saved_pk:
                    isSaved && savedPointIds
                      ? savedPointIds[screen.id] || null
                      : null,
                };
              }
              return screen;
            });

            // Only return updated app if replays were actually changed
            const hasChanges = updatedReplays.some(
              (screen, index) =>
                screen.is_saved !== app.replays[index].is_saved ||
                screen.is_saved_pk !== app.replays[index].is_saved_pk,
            );

            if (hasChanges) {
              return {
                ...app,
                replays: updatedReplays,
              };
            }

            return app;
          }),
        };
      });
    },
    [setCollectionApps],
  );

  // Function to handle removing app from collection

  const infiniteLoaderRef = useRef(null);
  const listRef = useRef<ListRef | null>(null);

  const isItemLoaded = (index: number) => {
    const loaded = index < (collectionApps?.results.length || 0);
    return loaded;
  };

  const itemCount = Math.max(
    1,
    (collectionApps?.results.length || 0) + (collectionApps?.next ? 1 : 0),
  );

  const getItemSize = (index: number) => {
    if (index !== 0 && !isItemLoaded(index)) {
      return 100; // Loading spinner height - increased from 40 to 240
    }
    return 750;
  };

  const loadMoreItems = useCallback(
    (startIndex: number, stopIndex: number) => {
      if (isFetchingMore || !collectionApps?.next) {
        return Promise.resolve();
      }

      return loadMoreApps();
    },
    [isFetchingMore, collectionApps?.next, loadMoreApps],
  );

  const Column = useCallback(
    ({ index, style }: RowProps) => {
      // Get current data from ref to always have latest data
      const currentCollectionApps = collectionAppsRef.current?.results || [];

      const appData = currentCollectionApps[index];
      if (!appData) {
        // Show loading spinner for items that haven't loaded yet
        if (!isItemLoaded(index)) {
          return (
            <div style={{ ...style, display: "flex", justifyContent: "center" }}>
              <Loader size={40} />
            </div>
          );
        }
        // Return null for items that should exist but don't
        return null;
      }

      return (
        <div style={style} key={`collection-${appData.id}-${index}`}>
          <ObserverWrapper index={index} onViewChange={() => {}}>
            <ExploreReplayRow
              key={appData.id}
              isOpen={openModalId === index}
              dataApi={appData}
              setIsPhonePageLoading={() => {}}
              ref={modalRef}
              isFirstGroup={false}
              currentOrganization={currentOrganization}
              isPaywallOnly={false}
              isPhonePageLoading={false}
              rowIndex={index}
              isCollection
              handleRemoveFromCollection={() =>
                handleRemoveFromCollection(appData.id)
              }
            />
          </ObserverWrapper>
        </div>
      );
    },
    // Only include stable dependencies, not the data itself - same pattern as ExplorAppsNew
    [
      openModalId,
      currentOrganization,
      // isFetchingMore,
      handleRemoveFromCollection,
    ],
  );

  // // Force list to re-render when data changes but prevent scroll reset
  // useEffect(() => {
  //   // Small delay to ensure data is updated
  //   const timer = setTimeout(() => {
  //     if (listRef.current) {
  //       // Force re-render without resetting scroll position
  //       listRef.current.resetAfterIndex(0);
  //     }
  //   }, 0);

  //   return () => clearTimeout(timer);
  // }, [collectionApps]);

  const { selectedScreens } = useSelectedScreens();

  // Add intersection observer for infinite loading in grid view
  const { ref: refIntersection, inView } = useInView({ threshold: 0 });

  useEffect(() => {
    if (inView && collectionApps?.next && !isFetchingMore) {
      loadMoreApps();
    }
  }, [inView, collectionApps?.next, isFetchingMore, loadMoreApps]);

  return (
    <div
      style={{
        height: "calc(100vh - 140px)",
        overflow: "hidden",
      }}
    >
      <AutoSizer>
        {({ height, width }: { height: number; width: number }) => (
          <InfiniteLoader
            ref={infiniteLoaderRef}
            isItemLoaded={isItemLoaded}
            itemCount={itemCount}
            loadMoreItems={loadMoreItems}
            threshold={1}
            minimumBatchSize={1}
          >
            {({
              onItemsRendered,
              ref,
            }: {
              onItemsRendered: (params: {
                overscanStartIndex: number;
                overscanStopIndex: number;
                visibleStartIndex: number;
                visibleStopIndex: number;
              }) => void;
              ref: (list: any) => void;
            }) => (
              <List
                height={height}
                itemCount={itemCount}
                itemSize={getItemSize}
                onItemsRendered={onItemsRendered}
                ref={(list: any) => {
                  ref(list);
                  listRef.current = list;
                }}
                width={width}
              >
                {Column}
              </List>
            )}
          </InfiniteLoader>
        )}
      </AutoSizer>
      {selectedScreens.length > 0 && (
        <SelectedScreensPopup
          type="appReplays"
          updateReplayScreensSavedStatus={updateReplayScreensSavedStatus}
        />
      )}
    </div>
  );
};

export default CollectionDetailViewApps;
